import { useEffect, useMemo, useRef, useState } from "react";
import DynamicForm, { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { Progress } from "reactstrap";

import styles from "./style.module.scss";
import buildAccountingPackageFormConfig from "./form";
import LoadingScreen from "@components/content/LoadingScreen";
import { showAlert } from "@utils/Alert";
import { AccountingPackageFormDataType } from "@/formTypes/AccountingPackageFormDataType";
import { AccountingPackageMode, buildAccountingPackageLabel } from "@utils/accountingPackage/reports";
import { AccountingPackageProgress, generateAccountingPackage } from "@utils/accountingPackage/generate";
import { OpenAccountingPackagePubSub } from "@utils/pubsubs/PubSub";

type AccountingPackageFormProps = {
  mode: AccountingPackageMode,
  defaultData: AccountingPackageFormDataType
}

const AccountingPackageForm = (props: AccountingPackageFormProps) => {
  const { mode, defaultData } = props;
  const formRef = useRef<DynamicFormRef>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<AccountingPackageProgress | null>(null);

  const formConfig = useMemo(() => buildAccountingPackageFormConfig(mode), [mode]);

  useEffect(() => {
    const now = new Date();
    formRef.current?.resetFormValue({
      month: String(now.getMonth() + 1).padStart(2, "0"),
      year: `${now.getFullYear()}`,
      ...defaultData
    })
  }, [mode, defaultData])

  const onSubmit = async (formData: AccountingPackageFormDataType) => {
    setIsLoading(true);
    setProgress(null);
    try {
      const result = await generateAccountingPackage({
        mode,
        year: `${formData.year}`,
        month: `${formData.month}`,
        emails: `${formData.emails}`,
        onProgress: setProgress
      });

      const period = buildAccountingPackageLabel(mode, `${formData.year}`, `${formData.month}`);
      showAlert(
        "success",
        result.attached
          ? `El paquete contable de ${period} ha sido enviado por correo`
          : `El paquete contable de ${period} supera el limite de un adjunto: se envio un enlace de descarga por correo`
      );

      OpenAccountingPackagePubSub.publish({ openOffCanvas: false });
    } catch (err) {
      if (err instanceof Error) {
        const errorMessage = err.message || "No fue posible generar el paquete contable";
        showAlert("danger", errorMessage)
      }
    } finally {
      setIsLoading(false);
      setProgress(null);
    }
  }

  return (
    <>
      <DynamicForm<AccountingPackageFormDataType>
        ref={formRef}
        id="accountingPackage"
        externalFormState={defaultData}
        formConfig={formConfig}
        onSubmit={onSubmit}
      >
      </DynamicForm>
      <LoadingScreen isLoading={isLoading}>
        {progress && (
          <>
            <Progress
              animated
              value={progress.completed}
              max={progress.total}
              data-testid="accounting-package-progress"
            >
              {`${progress.completed} / ${progress.total}`}
            </Progress>
            <p className={styles.progressStep}>
              {`${progress.step}...`}
            </p>
          </>
        )}
      </LoadingScreen>
    </>
  )
}

export default AccountingPackageForm;
